import { useNavigate, useParams } from "react-router-dom";
import { Form, message } from "antd";
import { useIntl } from "react-intl";
import { validateId } from "utils/validators";
import {
  useOrganizationDetailsQuery,
  useOrganizationUpdateMutation,
} from "@ticketingplatform/api/dist/organization";
import { ErrorCode } from "@ticketingplatform/api/dist/const/enums";
import useFormErrors from "hooks/useFormErrors";
import { handleFormErrors } from "utils/handleFormErrors";
import { messagesContent } from "const/messages/formErrors";
import { ROUTE_PATHS } from "const/paths";
import usePrompt from "hooks/usePrompt";

import { OrganizationFormValues } from "../OrganizationForm/types";

import { UseOrganizationEditReturnType } from "./types";

export const useOrganizationEdit = (): UseOrganizationEditReturnType => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const intl = useIntl();
  const validatedId = validateId(id);
  const navigate = useNavigate();

  const { setApolloError, onValuesChange, showErrorMessage } =
    useFormErrors(form);

  const organizationDetailsQuery = useOrganizationDetailsQuery({
    variables: { id: validatedId },
  });

  const organizationName =
    organizationDetailsQuery.data?.organization?.name || "";

  const [updateOrganization, updateOrganizationOpts] =
    useOrganizationUpdateMutation();

  usePrompt(updateOrganizationOpts.loading);

  const handleSubmit = (values: OrganizationFormValues) => {
    const { name } = values.input;

    return updateOrganization({
      variables: { id: validatedId, input: { name } },
      onCompleted: async () => {
        message.success(
          intl.formatMessage(
            { defaultMessage: 'Organization "{name}" updated.' },
            { name }
          )
        );

        navigate(ROUTE_PATHS.OrganizationsList, {
          state: {
            skipBlock: true,
          },
        });
      },
      onError: (error) => {
        setApolloError(error);

        const errorMessages = handleFormErrors(error, [
          ErrorCode.INTERNAL_ERROR,
          ErrorCode.ENTITY_EXISTS,
        ]);

        return showErrorMessage(errorMessages, name || "", messagesContent);
      },
    });
  };

  const loading =
    organizationDetailsQuery.loading || updateOrganizationOpts.loading;

  return {
    initialValues: { input: { name: organizationName } },
    loading,
    handleSubmit,
    form,
    onValuesChange: onValuesChange.onFormValuesChange,
  };
};
